import type { FC } from 'react';
import { Text } from '@launchpad-ui/components';

import { vars } from '../../../../css/vars';
import type { BaseContentProps } from '../Content';

export interface ReferenceContentProps extends BaseContentProps {
  /**
   * The content to display (Tag, Button, link, etc.)
   * If not provided, displays "Empty".
   */
  children?: React.ReactNode;
}

/**
 * Reference content component for use inside Cell.
 * A generic wrapper for interactive elements like Tags, Buttons, or links.
 *
 * @example
 * ```tsx
 * // With a Tag
 * <Cell kind="regular" size="default">
 *   <ReferenceContent>
 *     <Tag kind="secondary" shape="basic" iconLeft={<Icon />}>
 *       <Text lines="1">Session ID</Text>
 *     </Tag>
 *   </ReferenceContent>
 * </Cell>
 *
 * // With a Button
 * <Cell kind="regular" size="default">
 *   <ReferenceContent>
 *     <Button size="small">Click me</Button>
 *   </ReferenceContent>
 * </Cell>
 *
 * // Empty state
 * <Cell kind="regular" size="default">
 *   <ReferenceContent />
 * </Cell>
 * ```
 */
export const ReferenceContent: FC<ReferenceContentProps> = ({ children, className }) => {
  // If we have children, just render them
  if (children) {
    return <>{children}</>;
  }

  // Empty state: render "Empty" text
  return (
    <Text
      size="medium"
      elementType="span"
      className={className}
      maxLines={1}
      style={{
        whiteSpace: 'nowrap',
        color: vars.theme.interactive.fill.secondary.content.onDisabled,
      }}
    >
      Empty
    </Text>
  );
};
